import { Button } from '@material-ui/core';
import React, { FC, Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Visibility, DeleteForever } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { ModalDefaultDraggable } from '../../../molecules';
import { setSelectedContact, setSelectedLink, setShowAddEntity } from '../../../redux/slices/wikiSlice';
import { RootState } from '../../../redux/root';
import { JuriContactType, JuriLinkType } from '../../../api/jurisdiction';
import UrlLink from '../../clientModule/components/Common/Table/UrlLink';
import { urlRegex } from '../../../regex';
import LinkifiedText from '../../../atoms/LinkifiedText';

export type RecordRow = {
  field1: string;
  field2: string;
  id: number;
};

type Props = {
  subject: string;
  show: boolean;
  setShow: Function;
  headers: string[];
  rows: RecordRow[];
  onEdit: Function;
  onDelete: Function;
  children: any;
};
const TableArea: FC<Props> = ({ subject, show, setShow, headers, rows, onEdit, onDelete, children }) => {
  const { selectedContact, selectedLink } = useSelector((s: RootState) => s.wikiSlice);
  const [prefix, setPrefix] = useState<string>('Add');
  const dispatch = useDispatch();
  useEffect(() => {
    if ((selectedContact && Object.keys(selectedContact).length > 0) || (selectedLink && Object.keys(selectedLink).length > 0)) {
      setPrefix('Edit');
    } else {
      setPrefix('Add');
    }
  }, [selectedContact, selectedLink]);
  const onclick = id => {
    onEdit(id);
  };

  const deleteRecrd = id => {
    onDelete(id);
  };

  const onClose = () => {
    setShow(false);
    dispatch(setSelectedContact({} as JuriContactType));
    dispatch(setSelectedLink({} as JuriLinkType));
  };

  return (
    <TableClosure>
      <TableGrid>
        <Label>{subject}s</Label>
        <ButtonWrapper>
          <Button color="primary" variant="contained" onClick={() => setShow(true)}>
            {`Add ${subject}`}
          </Button>
        </ButtonWrapper>
        <HeaderCell>{headers[0]}</HeaderCell>
        <HeaderCell>{headers[1]}</HeaderCell>
        <HeaderCell>Actions</HeaderCell>
        {rows.map((r, index) => {
          // A lone URL renders as a link chip; text containing URLs is linkified.
          const isUrl = urlRegex.test(r.field2) && !/\s/.test(String(r.field2).trim());
          return (
            <Fragment key={index}>
              <Cell>
                <LinkifiedText value={r.field1} />
              </Cell>
              {isUrl ? (
                <UrlLink value={r.field2} />
              ) : (
                <Cell>
                  <LinkifiedText value={r.field2} />
                </Cell>
              )}
              <Cell>
                <Visibility onClick={e => onclick(r.id)} />
                <DeleteForever onClick={e => deleteRecrd(r.id)} />
              </Cell>
            </Fragment>
          );
        })}
      </TableGrid>
      {show && (
        <ModalDefaultDraggable title={`${prefix} ${subject}`} onClose={onClose}>
          <div>{children}</div>
        </ModalDefaultDraggable>
      )}
    </TableClosure>
  );
};

export default TableArea;

const TableClosure = styled.div`
  width: 100%;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 20px;
  box-sizing: border-box;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 73px;
  align-items: center;
  button {
    min-width: 140px;
    height: 36px;
    border-radius: 8px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0.01em;
  }
`;

const Label = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
`;

const HeaderCell = styled.div`
  background-color: var(--neutral-100);
  padding: 12px 12px;
  margin-top: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);

  &:first-of-type {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  &:last-of-type {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  text-align: right;
  grid-column: 2 / 4;
`;

const Cell = styled.div`
  font-size: 0.875rem;
  color: var(--mainText);
  padding: 14px 12px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  word-break: break-word;
  svg {
    cursor: pointer;
    color: var(--neutral-500);
    transition: color 0.15s ease;
  }
  svg:hover {
    color: var(--mainText);
  }
`;
